import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Beaker, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/catalog", label: "Shop" },
  { to: "/research", label: "Research" },
  { to: "/quality", label: "Quality" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = user?.name?.split(" ").map((n) => n[0]).slice(0, 2).join("") || "U";

  return (
    <header data-testid="site-navbar" className="glass-nav sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img src="/logo.webp" alt="" className="h-12 w-12 object-contain -my-1"/>
          <div className="leading-none">
            <div className="font-display text-lg font-semibold tracking-tight text-[#0e2f6b]">
              Peps<span className="text-blue-500">4</span>Pets
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-0.5">
              Research Grade · Vet
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm rounded-md transition-colors ${
                  isActive ? "text-blue-700 bg-blue-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-md object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-md bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                      {initials}
                    </div>
                  )}
                  <span className="text-sm text-slate-800 max-w-[120px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-md">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium text-slate-900">{user.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-portal" onClick={() => navigate("/dashboard")} className="cursor-pointer">
                  Customer Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer">
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="cursor-pointer text-red-600 focus:text-red-700"
                >
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                data-testid="nav-portal-btn"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md h-10 px-4"
              >
                Customer Portal
              </Button>
              <Button
                data-testid="nav-create-account-btn"
                onClick={() => navigate("/auth/register")}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-10 px-5"
              >
                Create Account
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
        >
          {open ? <X className="w-5 h-5" strokeWidth={1.75} /> : <Menu className="w-5 h-5" strokeWidth={1.75} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white/95">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md ${
                    isActive ? "text-blue-700 bg-blue-50" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/portal");
                }}
                className="mt-2 px-3 py-2 text-sm rounded-md bg-slate-900 text-white text-left"
              >
                Customer Portal
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/login");
                  }}
                  className="mt-2 px-3 py-2 text-sm rounded-md border border-slate-200 text-slate-800 text-left"
                >
                  Customer Portal
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/login");
                  }}
                  className="mt-1 px-3 py-2 text-sm rounded-md bg-blue-600 text-white text-left"
                >
                  Create Account
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
